/**
 * Motion del panel del promotor — count-up de montos + micro-celebración de logros.
 *
 * Fundamento (docs/diseño-ux/): el movimiento captura atención pre-atentiva (~250ms),
 * basta con animaciones cortas y baratas. La letra grande que CUENTA hacia arriba
 * (count-up) refuerza la sensación de ganancia. La micro-celebración invierte el
 * "brillo" en el PICO (peak-end), <1s, sin confeti.
 *
 * Reglas de perf: solo transform/opacity (el glow usa box-shadow, paint no layout,
 * aceptable porque es UNA cosa a la vez y <1s). prefers-reduced-motion hace
 * short-circuit (pinta el valor final, sin animar).
 */
import { montoHeroHtml } from '../views/_helpers.js';

function reducedMotion() {
    return (
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

// easeOutExpo: arranca rápido, frena suave — ideal para "el número que sube".
function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * Cuenta un MONTO (céntimos) hacia arriba en `el`, formateando con montoHeroHtml
 * cada frame. fitty se reescala SOLO al frame final (con tabular-nums el ancho
 * crece monótono, reescalar por frame es caro e innecesario en gama baja).
 * @param {HTMLElement} el
 * @param {number} centimos  valor final
 * @param {{ duracion?:number, delay?:number, onFinal?:(el:HTMLElement)=>void }} [opts]
 */
export function contarMonto(el, centimos, opts = {}) {
    if (!el) return;
    const final = Number(centimos) || 0;
    if (reducedMotion()) {
        el.innerHTML = montoHeroHtml(final);
        if (opts.onFinal) opts.onFinal(el);
        return;
    }
    const duracion = opts.duracion || 880;
    const delay = opts.delay != null ? opts.delay : 200;
    const inicio = 0;

    let startTs = null;
    function frame(ts) {
        if (startTs === null) startTs = ts;
        const elapsed = ts - startTs;
        if (elapsed < delay) {
            requestAnimationFrame(frame);
            return;
        }
        const p = Math.min(1, (elapsed - delay) / duracion);
        const valor = Math.round(inicio + (final - inicio) * easeOutExpo(p));
        el.innerHTML = montoHeroHtml(valor);
        if (p < 1) {
            requestAnimationFrame(frame);
        } else if (opts.onFinal) {
            opts.onFinal(el); // p===1: acá el caller reescala fitty una sola vez
        }
    }
    requestAnimationFrame(frame);
}

/**
 * Cuenta un ENTERO hacia arriba en `el` (afiliados, posición, etc.). Mismo
 * easeOutExpo que contarMonto pero sin formato de moneda.
 * @param {HTMLElement} el
 * @param {number} valor  valor final
 * @param {{ duracion?:number, delay?:number }} [opts]
 */
export function contarEntero(el, valor, opts = {}) {
    if (!el) return;
    const final = Number(valor) || 0;
    if (reducedMotion()) {
        el.textContent = String(final);
        return;
    }
    const duracion = opts.duracion || 800;
    const delay = opts.delay != null ? opts.delay : 150;
    let startTs = null;
    function frame(ts) {
        if (startTs === null) startTs = ts;
        const elapsed = ts - startTs;
        if (elapsed < delay) {
            requestAnimationFrame(frame);
            return;
        }
        const p = Math.min(1, (elapsed - delay) / duracion);
        el.textContent = String(Math.round(final * easeOutExpo(p)));
        if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
}

/**
 * Escribe `texto` carácter a carácter en `el` (efecto máquina de escribir, para la
 * bienvenida "entrá al mundo Golden Parce"). Segmenta por GRAFEMA (Intl.Segmenter)
 * para no partir emojis ni acentos pt/es; cae a Array.from en WebViews viejos.
 * Avanza por TIEMPO real, no por frame: a 30fps en gama baja no se ralentiza.
 * reducedMotion() pinta el texto entero de una. Devuelve Promise que resuelve al
 * terminar — para encadenar líneas y revelar el botón estrella al final.
 * @param {HTMLElement} el
 * @param {string} texto
 * @param {{ msPorCaracter?:number, delay?:number, signal?:AbortSignal }} [opts]
 * @returns {Promise<void>}
 */
export function escribirTexto(el, texto, opts = {}) {
    if (!el) return Promise.resolve();
    el.textContent = '';
    // Al terminar (por cualquier vía) se agrega .pm-listo, que el CSS usa para
    // OCULTAR el caret parpadeante (.pm-typewriter.pm-listo::after { display:none }).
    // Sin esto la barrita dorada queda parpadeando para siempre tras escribir.
    const marcarListo = () => el.classList.add('pm-listo');
    if (reducedMotion()) {
        el.textContent = texto;
        marcarListo();
        return Promise.resolve();
    }
    const Seg = typeof Intl !== 'undefined' && Intl.Segmenter ? Intl.Segmenter : null;
    const grafemas = Seg
        ? Array.from(new Seg(undefined, { granularity: 'grapheme' }).segment(texto), s => s.segment)
        : Array.from(texto);

    const msPorCaracter = opts.msPorCaracter || 45;
    const delay = opts.delay != null ? opts.delay : 0;

    return new Promise(resolve => {
        let i = 0;
        let startTs = null;
        function frame(ts) {
            if (opts.signal && opts.signal.aborted) {
                el.textContent = texto;
                marcarListo();
                return resolve();
            }
            if (startTs === null) startTs = ts;
            const elapsed = ts - startTs;
            if (elapsed < delay) {
                requestAnimationFrame(frame);
                return;
            }
            // Objetivo por tiempo: cuántos grafemas DEBERÍAN estar escritos a esta altura.
            const objetivo = Math.floor((elapsed - delay) / msPorCaracter);
            while (i < grafemas.length && i <= objetivo) {
                el.textContent += grafemas[i++];
            }
            if (i < grafemas.length) {
                requestAnimationFrame(frame);
            } else {
                marcarListo();
                resolve();
            }
        }
        requestAnimationFrame(frame);
    });
}

/**
 * Micro-celebración de un logro (peak-end, <1s, sin confeti). Glow que florece +
 * shine que barre + el elemento respira. Idempotente (no re-dispara si ya corre).
 * @param {HTMLElement} el
 * @param {'oro'|'verde'} [tono]
 */
export function celebrarLogro(el, tono = 'oro') {
    if (!el || reducedMotion()) return;
    if (el.dataset.celebrando === '1') return;
    el.dataset.celebrando = '1';
    el.classList.add('pm-celebra', tono === 'verde' ? 'pm-celebra-verde' : 'pm-celebra-oro');

    function limpiar() {
        el.classList.remove('pm-celebra', 'pm-celebra-oro', 'pm-celebra-verde');
        delete el.dataset.celebrando;
        el.removeEventListener('animationend', limpiar);
    }
    el.addEventListener('animationend', limpiar);
    // Red de seguridad por si animationend no dispara (elemento removido, etc.).
    setTimeout(limpiar, 1100);
}

/**
 * Detecta si un valor mejoró respecto al último visto (guardado en localStorage),
 * para celebrar SOLO el delta positivo (no en cada render — eso mataría el peak-end).
 * @param {string} clave   identificador del valor (ej. 'redito', 'ranking')
 * @param {number} valor   valor actual
 * @param {'subio'|'bajo'} [direccionBuena='subio']  qué dirección es "mejor"
 * @returns {boolean}  true si mejoró respecto al guardado
 */
export function mejoroRespectoAGuardado(clave, valor, direccionBuena = 'subio') {
    try {
        const k = `pm_motion_${clave}`;
        const previoStr = localStorage.getItem(k);
        localStorage.setItem(k, String(valor));
        if (previoStr === null) return false; // primera vez: no celebrar
        const previo = Number(previoStr);
        if (!Number.isFinite(previo)) return false;
        return direccionBuena === 'subio' ? valor > previo : valor < previo;
    } catch (_) {
        return false;
    }
}
